import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../../components/feature/Footer';
import Header from '../../components/feature/Header';
import { goldNews, type NewsItem } from '../../data/goldNews';
import { fetchMarketNews } from '../../services/marketDataApi';
import { marketNewsToNewsItem } from '../../utils/liveNews';

type NewsContentLanguage = keyof NewsItem['lang'];

function getContentLanguage(language: string): NewsContentLanguage {
  const normalized = language.split('-')[0];
  if (normalized === 'hk') return 'zh-HK';
  if (normalized === 'cn') return 'zh-CN';
  return 'en';
}

function getLabels(language: NewsContentLanguage) {
  if (language === 'zh-HK') {
    return {
      back: '返回市場新聞',
      notFoundTitle: '找不到新聞',
      notFoundText: '此新聞可能已被移除或網址不正確。',
    };
  }

  if (language === 'zh-CN') {
    return {
      back: '返回市场新闻',
      notFoundTitle: '找不到新闻',
      notFoundText: '此新闻可能已被移除或网址不正确。',
    };
  }

  return {
    back: 'Back to market news',
    notFoundTitle: 'News article not found',
    notFoundText: 'This article may have been removed or the URL may be incorrect.',
  };
}

export default function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const contentLanguage = getContentLanguage(i18n.resolvedLanguage || i18n.language);
  const fallbackLabels = getLabels(contentLanguage);
  const labels = {
    back: t('news.backToMarket', { defaultValue: fallbackLabels.back }),
    notFoundTitle: t('news.notFoundTitle', { defaultValue: fallbackLabels.notFoundTitle }),
    notFoundText: t('news.notFoundText', { defaultValue: fallbackLabels.notFoundText }),
  };
  const archivedItem = goldNews.find((newsItem) => newsItem.slug === slug);
  const prerenderedItem = window.__JLBHK_PRERENDERED_NEWS__?.slug === slug
    ? window.__JLBHK_PRERENDERED_NEWS__
    : undefined;
  const [remoteItem, setRemoteItem] = useState<NewsItem | undefined>();
  const [remoteLoading, setRemoteLoading] = useState(false);
  const item = archivedItem || prerenderedItem || remoteItem;

  useEffect(() => {
    if (item || !slug) return undefined;
    const match = slug.match(/^market-news-(\d+)$/);
    if (!match) return undefined;

    const controller = new AbortController();
    const wantedId = Number(match[1]);
    setRemoteLoading(true);

    void fetchMarketNews(
      { type: 'ALL', page: 0, size: 100, language: i18n.resolvedLanguage || i18n.language },
      controller.signal,
    )
      .then((response) => {
        const marketItem = response.items.find((candidate) => Number(candidate.id) === wantedId);
        if (marketItem) setRemoteItem(marketNewsToNewsItem(marketItem, contentLanguage));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setRemoteLoading(false);
      });

    return () => controller.abort();
  }, [contentLanguage, i18n.language, i18n.resolvedLanguage, item, slug]);

  if (remoteLoading && !item) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 pb-24 pt-36 text-center md:px-6 md:pt-44">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-yellow-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 pb-24 pt-36 text-center md:px-6 md:pt-44">
          <h1 className="text-4xl font-bold text-gray-950">{labels.notFoundTitle}</h1>
          <p className="mt-4 text-gray-600">{labels.notFoundText}</p>
          <Link
            to="/grand-opening"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-gray-950 px-5 py-3 font-bold text-white transition hover:bg-yellow-500 hover:text-gray-950"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            {labels.back}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const block = item.lang[contentLanguage];
  const locale = t('news.dateLocale', { defaultValue: contentLanguage === 'zh-HK' ? 'zh-HK' : contentLanguage === 'zh-CN' ? 'zh-CN' : 'en-GB' });
  const publishedDate = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Hong_Kong',
  }).format(new Date(item.publishedAt));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="pb-20 pt-28 md:pb-28 md:pt-36">
        <article className="container mx-auto px-4 md:px-6" lang={contentLanguage}>
          <div className="mx-auto max-w-5xl">
            <Link
              to="/grand-opening"
              className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 transition hover:text-yellow-600"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              {labels.back}
            </Link>

            <header className="mt-7 overflow-hidden rounded-3xl border border-gray-200 bg-gray-950 text-white shadow-xl">
              {item.image && (
                <div className="aspect-[16/8] overflow-hidden bg-gray-900">
                  <img
                    src={item.image}
                    alt={block.title}
                    className="h-full w-full object-cover"
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>
              )}

              <div className="px-6 py-8 sm:px-10 sm:py-10 lg:px-14 lg:py-12">
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-300">
                  <span className="rounded-full border border-yellow-300/30 bg-yellow-300/10 px-3 py-1 font-semibold text-yellow-200">
                    {t(`news.filter.${item.category === 'central-bank' ? 'centralBank' : item.category}`, { defaultValue: item.category })}
                  </span>
                  <time dateTime={item.publishedAt}>{publishedDate}</time>
                  <span aria-hidden="true">·</span>
                  <span>{item.source}</span>
                </div>

                <h1 className="mt-6 text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
                  {block.title}
                </h1>
                <p className="mt-6 max-w-4xl text-lg leading-8 text-gray-200">{block.summary}</p>
              </div>
            </header>

            <div className="mt-8 rounded-3xl border border-gray-200 bg-white px-6 py-9 shadow-sm sm:px-10 sm:py-12 lg:px-16">
              <div className="prose prose-lg mx-auto max-w-3xl text-gray-700">
                {block.content
                  .split(/\n{2,}/)
                  .filter(Boolean)
                  .map((paragraph) => (
                    <p key={paragraph} className="mb-6 text-base leading-8 sm:text-lg sm:leading-9">
                      {paragraph}
                    </p>
                  ))}
              </div>

              <div className="mx-auto mt-10 flex max-w-3xl flex-wrap gap-2 border-t border-gray-200 pt-7">
                {item.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600">
                    #{tag}
                  </span>
                ))}
              </div>

            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
